import { useLayoutEffect, useRef } from 'react'
import {
    useLocation,
    useNavigate,
    useNavigationType,
    type Location
} from 'react-router-dom'

const toUrl = (location: Location) =>
    `${location.pathname}${location.search}${location.hash}`

/**
 * Blocks browser Back/Forward (including Edge edge-swipe) on the kiosk.
 *
 * In-app "back" controls navigate to an explicit URL (via replace) and are
 * unaffected.
 *
 * Edge swipe fires a real popstate. React Router 7 also wraps location
 * updates in startTransition, so undoing the POP *after* the popped route
 * has committed lets that route run mount effects (welcome resetOrder).
 *
 * Restore the locked URL + React Router history.state in the capture
 * phase so Router's bubble-phase listener never sees the popped route.
 * Never write a raw history.state object — React Router stores
 * `{ usr, key, idx }` and overwriting that lets a later swipe walk the stack.
 */
export function useKioskHistoryGuard() {
    const location = useLocation()
    const navigate = useNavigate()
    const navigationType = useNavigationType()
    const lockedLocationRef = useRef(location)
    const lockedHistoryStateRef = useRef(window.history.state)

    useLayoutEffect(() => {
        const restoreLockedHistory = () => {
            const locked = lockedLocationRef.current
            window.history.pushState(
                lockedHistoryStateRef.current,
                '',
                toUrl(locked)
            )
        }

        window.addEventListener('popstate', restoreLockedHistory, true)
        return () => {
            window.removeEventListener('popstate', restoreLockedHistory, true)
        }
    }, [])

    useLayoutEffect(() => {
        if (navigationType === 'POP') {
            const locked = lockedLocationRef.current
            const url = toUrl(locked)
            // Fallback if the capture listener missed (e.g. listener not
            // mounted yet). Replace only — do not raw-pushState.
            if (toUrl(location) !== url) {
                navigate(url, { replace: true, state: locked.state })
            }
            return
        }

        lockedLocationRef.current = location
        lockedHistoryStateRef.current = window.history.state
    }, [location, navigationType, navigate])
}
